<template>
  <div class="confirm__wrapper">
    <p class="confirm__title">One more step</p>
    <div class="confirm__container">
      <ul class="confirm__confirm-text-list">
        <li
          v-for="(text, index) in confirmText"
          :key="index"
          data-testid="confirm-text-list-item"
        >
          {{ text }}
        </li>
      </ul>
      <Checkbox
        v-model="confirmation"
        class="confirm__checkbox"
        input-id="confirmationId"
        label="I understand and agree to the terms of installation"
        full-width
        data-testid="confirmation-checkbox"
      />
      <p
        v-if="showCheckboxError"
        class="confirm__checkbox-error"
        data-testid="checkbox-error"
      >
        Please agree to the terms to continue
      </p>
      <Accordion
        :accordion-content="installationDetails"
        data-test-id="confirm-details-accordion"
      >
        <ConfirmDetailsBlock
          v-for="(detail, index) in details"
          :key="index"
          :title="detail.title"
          :texts="detail.texts"
          :link="detail.link"
        />
      </Accordion>
      <p class="confirm__reading-type-label">
        Choose how you'd like to view your energy use in your account:
      </p>
      <RadioButtonCard
        v-model="selectedReadingType"
        :class="[
          'confirm__reading-type-card',
          { 'confirm__reading-type-card--error': showReadingError },
        ]"
        label="Half-hourly readings"
        value="half-hourly"
        variant="primary"
        display-badge
        badge-content="Recommended"
        selectable
        data-testid="half-hourly-option"
      >
        See a detailed breakdown of your energy use throughout the day, helping
        you spot where to reduce waste and save money.
      </RadioButtonCard>
      <RadioButtonCard
        v-model="selectedReadingType"
        :class="[
          'confirm__reading-type-card',
          { 'confirm__reading-type-card--error': showReadingError },
        ]"
        label="Daily readings"
        value="daily"
        variant="primary"
        selectable
        data-testid="daily-option"
      >
        See your energy use summarised each day.
      </RadioButtonCard>
      <p
        v-if="showReadingError"
        class="confirm__reading-error"
        data-testid="reading-error"
      >
        Please select an option to continue
      </p>
      <p class="confirm__reading-type-footnote">
        With both options your smart meter will also send us strictly anonymous
        half-hourly readings. This helps us balance electricity supply and
        demand, contributing to a greener grid – and helping to keep your energy
        costs down.
      </p>
      <p class="confirm__reading-type-footnote">
        You can change these settings at any time in your account.
      </p>
      <AppButton
        class="confirm__next-button"
        :disabled="isLoading"
        :loading="isLoading"
        data-testid="confirm-continue-button"
        @click="nextStep"
      >
        <span>Book Your Installation</span>
        <span v-if="!isLoading" class="confirm__icon icon icon-arrow-right" />
      </AppButton>
      <FeedbackDialog
        v-if="showError"
        class="confirm__error-message"
        variant="negative"
        :message="errorMessage"
      />
    </div>
  </div>
</template>

<script>
import ConfirmDetailsBlock from "@/components/ConfirmDetailsBlock.vue"
import Accordion from "@soenergy/frontend-library/src/components/Accordion.vue"
import AppButton from "@soenergy/frontend-library/src/components/AppButton.vue"
import Checkbox from "@soenergy/frontend-library/src/components/Checkbox.vue"
import RadioButtonCard from "@soenergy/frontend-library/src/components/RadioButtonCard.vue"
import dayjs from "dayjs"
import { mapGetters, mapState } from "vuex"
import FeedbackDialog from "@soenergy/frontend-library/src/components/FeedbackDialog.vue"
import { MeterReadingFrequency } from "@/constants"

export default {
  components: {
    Accordion,
    Checkbox,
    ConfirmDetailsBlock,
    AppButton,
    FeedbackDialog,
    RadioButtonCard,
  },
  emits: ["go-forward"],
  data() {
    return {
      confirmText: [
        "Someone aged 18 or over will need to be present for the whole installation, even if the meters are outside.",
        "There must be a clear working area around the meter(s) on the day of the appointment.",
        "If the engineer can't access the property, meters, or main fuse, the appointment will need to be rescheduled.",
        "Changes or cancellations must be made 48 hours before the appointment date.",
      ],
      confirmation: false,
      selectedReadingType: null,
      isSubmitting: false,
      showCheckboxError: false,
      showReadingError: false,
      installationDetails: [
        {
          heading: "Installation details",
          open: false,
        },
      ],
      MeterReadingFrequency,
    }
  },
  computed: {
    ...mapGetters(["contact"]),
    ...mapState(["submitStatus", "submitError"]),
    isLoading() {
      return this.isSubmitting || this.submitStatus === "PENDING"
    },
    showError() {
      return this.submitStatus === "ERROR"
    },
    errorMessage() {
      if (this.submitError?.includes("Missing MSN")) {
        return "We can't complete your booking online. Please contact us on 0330 111 5050 so we can help."
      }
      return "Something went wrong. Please try again, or contact us if it keeps happening."
    },
    slotInfo() {
      const selectedAppointmentSlot =
        this.$store.state.appointment.selectedAppointmentSlot
      const date = dayjs(selectedAppointmentSlot.date).format("ddd D MMM YYYY")
      return `${date} - ${selectedAppointmentSlot.time["time-slot"]}`
    },
    details() {
      const questionnaire = this.$store.state.questionnaire
      const ladderRequired = questionnaire.meterAboveHeadHeight.answer
        ? "Ladder required"
        : "Ladder not required"
      const lessThanSixInchesAroundMeterAnswer = questionnaire
        .lessThanSixInchesAroundMeter.answer
        ? "There is less than 15cm / 6 inches of space around one or more meters"
        : "There is not less than 15cm / 6 inches of space around all meters"
      return [
        {
          link: "choose-appointment",
          title: "Appointment date & time",
          texts: [this.slotInfo],
        },
        {
          link: "contact-details",
          title: "Adult present for appointment",
          texts: [
            `${this.contact.firstName} ${this.contact.lastName}`,
            this.contact.phone,
            this.contact.email,
          ],
        },
        {
          link: "meter-access",
          title: "Meter details",
          texts: [
            questionnaire.gasMeterLocation.answer &&
              `Gas meter is ${questionnaire.gasMeterLocation.answer.toLowerCase()}`,
            questionnaire.distanceBetweenMeters.answer &&
              `Meters are ${questionnaire.distanceBetweenMeters.answer.toLowerCase()} apart`,
            ladderRequired,
            lessThanSixInchesAroundMeterAnswer,
          ].filter(Boolean),
        },
        {
          link: "engineer-access",
          title: "Engineer access",
          texts: [
            questionnaire.parkingDetails.answer,
            questionnaire.petDetails.answer,
          ],
        },
      ]
    },
  },
  watch: {
    submitStatus(status) {
      // Re-enable only on error so the customer can retry. On success we keep
      // the button disabled through navigation to prevent a duplicate booking.
      if (status === "ERROR") {
        this.isSubmitting = false
      }
    },
    confirmation(val) {
      if (val) this.showCheckboxError = false
    },
    selectedReadingType(val) {
      if (val) this.showReadingError = false
    },
  },
  methods: {
    nextStep() {
      if (this.isLoading) return

      this.showCheckboxError = !this.confirmation
      this.showReadingError = !this.selectedReadingType

      if (this.showCheckboxError || this.showReadingError) return

      const frequencyMap = {
        "half-hourly": MeterReadingFrequency.HALF_HOURLY,
        daily: MeterReadingFrequency.DAILY,
      }
      this.$store.commit(
        "SET_METER_READING_FREQUENCY",
        frequencyMap[this.selectedReadingType]
      )
      this.isSubmitting = true
      this.$emit("go-forward")
    },
  },
}
</script>

<style lang="scss" scoped>
.confirm {
  font-size: $font-size-4;
  &__wrapper {
    margin-bottom: $space-12;
    ::v-deep .form-group__input-group {
      max-width: 100%;
    }
  }
  &__icon {
    margin-left: $space-1;
  }
  &__title {
    font-size: $font-size-5;
    font-weight: $weight-medium;
    margin-bottom: $space-6;

    @include md {
      font-size: $font-size-6;
      margin-bottom: $space-7;
    }
  }

  &__text {
    margin-top: $space-3;
    font-size: $font-size-3;

    @include md {
      font-size: $font-size-4;
    }
  }
  &__container {
    display: flex;
    flex-direction: column;
  }

  &__confirm-text-list {
    font-size: $font-size-3;
    list-style-type: disc;
    padding-left: $space-6;

    li {
      margin-bottom: $space-5;
      padding-left: $space-3;
    }

    @include md {
      font-size: $font-size-4;
    }
  }

  &__checkbox {
    margin: $space-2 $space-0 $space-5 $space-0;

    @include md {
      margin-bottom: $space-3;
    }
  }

  &__checkbox-error {
    color: #c40021;
    font-size: 14px;
    margin-top: $space-1;
    margin-bottom: $space-3;
  }

  &__reading-type-label {
    font-size: $font-size-3;
    font-weight: $weight-medium;
    margin: $space-7 $space-0 $space-8 $space-0;

    @include md {
      font-size: $font-size-4;
    }
  }

  &__reading-type-card {
    font-size: $font-size-3;
    margin-bottom: $space-5;

    ::v-deep .radio-button-card__badge {
      left: unset;
      right: -0.1rem;
    }

    ::v-deep .radio-button-card__heading {
      font-size: $font-size-4;
    }

    &--error {
      box-shadow: 0 0 0 4px #ff7875;
    }
  }

  &__reading-error {
    color: #c40021;
    font-size: 18px;
    margin-top: $space-1;
    margin-bottom: $space-3;
  }

  &__reading-type-footnote {
    font-size: $font-size-3;
    margin: $space-2 $space-0 $space-3 $space-0;

    @include md {
      margin-top: $space-3;
    }
  }

  &__next-button {
    width: 100%;
    margin-top: $space-6;

    @include md {
      margin: $space-7 $space-0 $space-4 $space-0;
    }
  }

  &__error-message {
    margin-top: $space-4;
  }
}

::v-deep .checkbox__input:checked + .checkbox__label {
  font-weight: $weight-normal;
}

::v-deep .checkbox__label {
  font-size: $font-size-2;
  margin-left: 0.35rem;

  @include md {
    font-size: $font-size-3;
  }
}

::v-deep .accordion__heading h4 {
  font-size: $font-size-4;
  font-weight: $weight-medium;

  @include md {
    font-size: $font-size-5;
  }
}

::v-deep .accordion__content .confirm-details-block:last-child {
  margin-bottom: $space-8;
}
</style>
